import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import {
  fetchDispensingSettings, fetchDispensingSettingsAdmin, fetchNearExpiryReport, updateDispensingSettings,
} from './api';
import type { DispensingSettings } from './types';

/**
 * Used until the settings load (and if they can't): the server's own default
 * window, and 'other' alone so a later-batch line can still be explained with
 * remarks — the full list comes from the server (utils/pharmacyExpiry.js).
 */
export const DEFAULT_DISPENSING_SETTINGS: DispensingSettings = {
  near_expiry_days: 90,
  batch_selection_reasons: [{ value: 'other', label: 'Other' }],
};

/**
 * The near-expiry window and later-batch reason list, as every dispensing
 * screen sees them — tenant-wide, so no branchVersion. Always returns a
 * usable value: a failed read falls back to the server's default window
 * rather than hiding the warning, and the sale engine enforces the reason
 * rule regardless of what the screen shows.
 */
export function useDispensingSettings(): DispensingSettings {
  const query = useQuery({ queryKey: keys.dispensingSettings(), queryFn: fetchDispensingSettings, staleTime: 5 * 60_000 });
  return query.data ?? DEFAULT_DISPENSING_SETTINGS;
}

export function useDispensingSettingsAdmin() {
  return useQuery({ queryKey: keys.dispensingSettingsAdmin(), queryFn: fetchDispensingSettingsAdmin });
}

/** Refreshes both reads, so an open dispensing screen follows the new window. */
export function useUpdateDispensingSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateDispensingSettings,
    onSuccess: (settings) => {
      queryClient.setQueryData(keys.dispensingSettingsAdmin(), settings);
      queryClient.invalidateQueries({ queryKey: keys.dispensingSettings(), exact: false });
      queryClient.invalidateQueries({ queryKey: keys.pharmacy.nearExpiryAll(), exact: false });
    },
  });
}

/** Branch-scoped — see useInventory.ts for why branchVersion is appended. */
export function useNearExpiryReport(selectionsDays: number, storeId?: number) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.nearExpiry(selectionsDays), storeId ?? 'all', branchVersion],
    queryFn: () => fetchNearExpiryReport(selectionsDays, storeId),
  });
}
